/**
 * PageHeader Component
 *
 * Reusable page header with title, breadcrumbs, and action buttons
 */

import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, Typography, Space, Flex } from 'antd';

const { Title, Text } = Typography;

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs,
  actions,
}) => {
  return (
    <div className="mb-4 pb-4 border-b border-gray-200">
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb
          className="mb-4"
          items={breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;

            if (isLast || !crumb.path) {
              return {
                title: crumb.label,
              };
            }

            return {
              title: <Link to={crumb.path}>{crumb.label}</Link>,
            };
          })}
        />
      )}

      {/* Title and Actions */}
      <Flex
        justify="space-between"
        align="flex-start"
        gap={16}
        wrap="wrap"
      >
        {/* Title Section */}
        <div className="flex-1">
          <Title level={2} >
            {title}
          </Title>

          {subtitle && (
            <Text type="secondary" className="max-w-[600px] block">
              {subtitle}
            </Text>
          )}
        </div>

        {/* Actions */}
        {actions && (
          <Space wrap size={12}>
            {actions}
          </Space>
        )}
      </Flex>
    </div>
  );
};

export default PageHeader;
